/**
 * Confirmation page - shown after entry submission
 */
import { BaseElement } from '../base-element';
import { Signal } from 'signal-polyfill';
import { entriesApi } from '../../api/entries.api';
import { canAccessSecretaryFeatures } from '../../state/auth.state';
import { navigate } from '../../router';
import type { EntryDetailDto } from '../../api/types';

type PageState = 'loading' | 'loaded' | 'error';

export class ConfirmationPage extends BaseElement {
  private entry = new Signal.State<EntryDetailDto | null>(null);
  private loading = new Signal.State(true);
  private errorMessage = new Signal.State<string | null>(null);
  private supportId = new Signal.State<string | null>(null);
  private trialEventId = '';
  private pageState = new Signal.Computed<PageState>(() => {
    if (this.loading.get()) return 'loading';
    if (this.errorMessage.get()) return 'error';
    return 'loaded';
  });

  static get observedAttributes(): string[] {
    return ['entry-id', 'trial-event-id'];
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    if (name === 'trial-event-id') {
      this.trialEventId = newValue || '';
    }
  }

  connectedCallback(): void {
    this.updateRender();
    void this.loadEntry();
  }

  private async loadEntry(): Promise<void> {
    const entryId = this.getAttribute('entry-id');
    if (!entryId || !this.trialEventId) {
      this.loading.set(false);
      this.errorMessage.set('Entry ID or Trial Event ID is missing.');
      this.updateRender();
      return;
    }

    this.loading.set(true);
    this.errorMessage.set(null);
    this.supportId.set(null);
    this.updateRender();

    try {
      const response = await entriesApi.getById(this.trialEventId, entryId);

      if (response.error || !response.data) {
        this.entry.set(null);
        this.errorMessage.set(response.error?.title || 'Unable to load confirmation.');
        this.supportId.set(response.supportId || response.error?.traceId || null);
      } else {
        this.entry.set(response.data);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to load confirmation.';
      this.entry.set(null);
      this.errorMessage.set(message);
    } finally {
      this.loading.set(false);
      this.updateRender();
    }
  }

  private renderLoading(): string {
    return `
      <div class="px-4 py-10" data-testid="confirmation-loading">
        <div class="relative mx-auto flex min-h-[380px] w-full max-w-4xl flex-col items-center justify-center overflow-hidden rounded-[32px] border border-neutral-200 bg-white px-6 py-12 text-center shadow-[0_24px_70px_rgba(48,52,37,0.12)] dark:border-white/10 dark:bg-surface-dark sm:px-10">
          <div class="pointer-events-none absolute -left-12 top-0 h-40 w-40 rounded-full bg-primary-100 blur-3xl dark:bg-primary-900/20"></div>
          <div class="pointer-events-none absolute -right-10 bottom-0 h-44 w-44 rounded-full bg-secondary-100 blur-3xl dark:bg-secondary-900/20"></div>
          <div class="relative inline-flex items-center rounded-full bg-primary-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-700 dark:bg-primary-500/15 dark:text-primary-200">Venom Dog Sports</div>
          <div class="relative mb-4 mt-6 flex h-16 w-16 items-center justify-center rounded-full bg-success-50 text-success-500 ring-8 ring-white dark:bg-success-500/10 dark:ring-surface-dark">
            <dt-spinner size="large"></dt-spinner>
          </div>
          <h1 class="relative text-3xl font-semibold text-neutral-900 dark:text-white">Loading your confirmation</h1>
          <p class="relative mt-3 max-w-lg text-sm leading-7 text-neutral-600 dark:text-neutral-300">We’re pulling in the submitted entry summary, confirmation details, and next steps for the handler.</p>
        </div>
      </div>
    `;
  }

  private renderError(): string {
    const message = this.errorMessage.get() ?? 'Unable to load confirmation.';
    const supportId = this.supportId.get();

    return `
      <div class="px-4 py-10" data-testid="confirmation-error">
        <div class="relative mx-auto flex min-h-[380px] w-full max-w-4xl flex-col items-center justify-center overflow-hidden rounded-[32px] border border-neutral-200 bg-white px-6 py-12 text-center shadow-[0_24px_70px_rgba(48,52,37,0.12)] dark:border-white/10 dark:bg-surface-dark sm:px-10">
          <div class="pointer-events-none absolute -left-12 top-0 h-40 w-40 rounded-full bg-error-50 blur-3xl dark:bg-error-500/10"></div>
          <div class="pointer-events-none absolute -right-10 bottom-0 h-44 w-44 rounded-full bg-secondary-100 blur-3xl dark:bg-secondary-900/20"></div>
          <div class="relative inline-flex items-center rounded-full bg-primary-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-700 dark:bg-primary-500/15 dark:text-primary-200">Venom Dog Sports</div>
          <div class="relative mb-6 mt-6 flex h-16 w-16 items-center justify-center rounded-full bg-error-50 text-error-500 ring-8 ring-white dark:bg-error-500/10 dark:ring-surface-dark">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-8 w-8">
              <path fill-rule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.75a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clip-rule="evenodd"/>
            </svg>
          </div>
          <p class="relative text-xs font-semibold uppercase tracking-[0.18em] text-error-700 dark:text-error-300">Support is ready</p>
          <h1 class="relative mb-2 mt-3 text-3xl font-semibold text-neutral-900 dark:text-white">Unable to load confirmation</h1>
          <p class="relative mb-2 max-w-xl text-sm leading-7 text-neutral-600 dark:text-neutral-300">${this.esc(message)}</p>
          ${supportId ? `<p class="relative rounded-full bg-neutral-100 px-4 py-2 text-xs font-medium text-neutral-600 dark:bg-neutral-900 dark:text-neutral-300" data-testid="support-id">Support ID: ${this.esc(supportId!)}</p>` : ''}
          <p class="relative mt-6 max-w-xl text-sm leading-7 text-neutral-500 dark:text-neutral-400">If you contact support, include the ID above so the team can trace the exact confirmation failure quickly.</p>
        </div>
      </div>
    `;
  }

  private renderSuccess(entry: EntryDetailDto): string {
    const canViewEntryDetails = canAccessSecretaryFeatures.get();
    const entryNumber = entry.entryNumber ?? '—';
    const printedEntryNumber = entry.entryNumber
      ? entry.entryNumber.slice(entry.entryNumber.lastIndexOf('-') + 1)
      : null;
    const trialName = entry.trialEvent?.name ?? 'Trial event';
    const dogName = entry.dog.callName ?? '—';

    return `
      <div class="px-4 py-10" data-testid="confirmation-success">
        <div class="relative mx-auto w-full max-w-5xl overflow-hidden rounded-[32px] border border-neutral-200 bg-white shadow-[0_24px_70px_rgba(48,52,37,0.12)] dark:border-white/10 dark:bg-surface-dark">
          <div class="pointer-events-none absolute -left-14 top-0 h-44 w-44 rounded-full bg-primary-100 blur-3xl dark:bg-primary-900/20"></div>
          <div class="pointer-events-none absolute -right-12 bottom-0 h-48 w-48 rounded-full bg-success-50 blur-3xl dark:bg-success-500/10"></div>
          <div class="relative grid gap-8 px-6 py-12 lg:grid-cols-[1.15fr_0.85fr] lg:items-start lg:px-10">
            <div class="text-center lg:text-left">
              <div class="inline-flex items-center rounded-full bg-primary-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary-700 dark:bg-primary-500/15 dark:text-primary-200">Venom Dog Sports</div>
              <div class="mx-auto mb-6 mt-6 flex h-20 w-20 items-center justify-center rounded-full bg-success-50 text-success-500 ring-8 ring-white dark:bg-success-500/10 dark:ring-surface-dark lg:mx-0">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="h-10 w-10">
                  <path fill-rule="evenodd" d="M2.25 12.75a.75.75 0 01.75-.75h1.542a.75.75 0 010 1.5H3.75v1.542a.75.75 0 01-1.5 0v-2.292zm18 0a.75.75 0 00-.75-.75h-1.542a.75.75 0 000 1.5h1.542v1.542a.75.75 0 001.5 0v-2.292zM6.53 11.47a.75.75 0 10-1.06 1.06l4.5 4.5a.75.75 0 001.06 0l8-8a.75.75 0 10-1.06-1.06L10.5 15.44l-3.97-3.97z" clip-rule="evenodd" />
                </svg>
              </div>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-success-700 dark:text-success-300">Submission complete</p>
              <h1 class="mt-3 text-3xl font-semibold text-neutral-900 dark:text-white">Entry Submitted</h1>
              <p class="mt-3 max-w-xl text-sm leading-7 text-neutral-600 dark:text-neutral-300">Your handler submission is locked in. We’ll send the branded confirmation email shortly and keep the PDF ready for review.</p>
              <div class="mt-6 rounded-2xl border border-secondary-200/70 bg-secondary-50/80 p-5 text-left dark:border-secondary-500/20 dark:bg-secondary-500/10">
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-secondary-700 dark:text-secondary-200">What’s next</p>
                <ul class="mt-3 space-y-2 text-sm leading-6 text-neutral-700 dark:text-neutral-200">
                  <li>Check your inbox for the entry confirmation email and PDF link.</li>
                  <li>Keep your registration number handy if you need secretary support.</li>
                  <li>Return to the registration flow if you have another dog to enter.</li>
                </ul>
              </div>
            </div>
            <div class="space-y-4">
              <div class="rounded-[28px] border border-success-200 bg-success-50/90 p-6 text-center shadow-xs dark:border-success-500/25 dark:bg-success-500/10">
                <div class="text-xs font-semibold uppercase tracking-[0.3em] text-success-700 dark:text-success-300">Entry Number</div>
                <div class="mt-2 text-2xl font-bold text-neutral-900 dark:text-white" data-testid="entry-number">${this.esc(entryNumber)}</div>
                ${printedEntryNumber ? `<div class="mt-1 text-xs text-neutral-500 dark:text-neutral-400" data-testid="entry-number-printed">appears as ${this.esc(printedEntryNumber)} on the printed form</div>` : ''}
                <div class="mt-4 text-sm font-semibold text-neutral-800 dark:text-neutral-200" data-testid="trial-name">${this.esc(trialName)}</div>
                <div class="text-sm text-neutral-500 dark:text-neutral-400" data-testid="dog-name">${this.esc(dogName)}</div>
              </div>
              <div class="rounded-[28px] border border-neutral-200 bg-neutral-50/80 p-6 text-left dark:border-white/10 dark:bg-neutral-950/40">
                <p class="text-xs font-semibold uppercase tracking-[0.18em] text-neutral-500">Confirmation details</p>
                <dl class="mt-4 space-y-3 text-sm text-neutral-700 dark:text-neutral-200">
                  <div class="flex items-start justify-between gap-4">
                    <dt class="font-semibold text-neutral-800 dark:text-white">Trial</dt>
                    <dd class="text-right">${this.esc(trialName)}</dd>
                  </div>
                  <div class="flex items-start justify-between gap-4">
                    <dt class="font-semibold text-neutral-800 dark:text-white">Dog</dt>
                    <dd class="text-right">${this.esc(dogName)}</dd>
                  </div>
                  <div class="flex items-start justify-between gap-4">
                    <dt class="font-semibold text-neutral-800 dark:text-white">Status</dt>
                    <dd class="text-right">Submitted</dd>
                  </div>
                </dl>
              </div>
              <div class="flex flex-wrap items-center justify-center gap-3 lg:justify-start">
                ${canViewEntryDetails ? `<button class="inline-flex items-center justify-center rounded-xl border border-neutral-200 px-5 py-2 text-sm font-semibold text-neutral-700 transition-colors hover:bg-neutral-50 dark:border-neutral-700 dark:text-neutral-200 dark:hover:bg-neutral-800" data-testid="view-entry-btn">View Entry Details</button>` : ''}
                <button class="inline-flex items-center justify-center rounded-xl bg-primary-500 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary-600" data-testid="submit-another-btn">Submit Another Entry</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private updateRender(): void {
    const state = this.pageState.get();
    let content = '';

    if (state === 'loading') {
      content = this.renderLoading();
    } else if (state === 'error') {
      content = this.renderError();
    } else if (this.entry.get()) {
      content = this.renderSuccess(this.entry.get()!);
    }

    this.render(content);
    this.attachEventListeners();
  }

  private attachEventListeners(): void {
    const viewBtn = this.$('[data-testid="view-entry-btn"]');
    if (viewBtn) {
      viewBtn.addEventListener('click', () => {
        if (!canAccessSecretaryFeatures.get()) return;

        const entry = this.entry.get();
        if (!entry) return;
        const trialEventId = entry.trialEvent?.trialEventId;
        if (!trialEventId) return;
        navigate(`/secretary/entries/${trialEventId}/${entry.entryId}`);
      });
    }

    const submitAnotherBtn = this.$('[data-testid="submit-another-btn"]');
    if (submitAnotherBtn) {
      submitAnotherBtn.addEventListener('click', () => {
        const entry = this.entry.get();
        const trialEventId = entry?.trialEvent?.trialEventId;
        navigate(trialEventId ? `/trials/${trialEventId}/register` : '/trials');
      });
    }
  }
}

customElements.define('dt-confirmation-page', ConfirmationPage);
